import React from "react";
import { useSelector } from "react-redux";
import { getDvSourceImageUrl } from "../../functions/themeAssets";
import useLeaderboardData from "./useLeaderboardData";

/**
 * The trophy title + glass card + rows table — the actual leaderboard UI.
 * Callers own the page-level backdrop (`.lb-stage`/`.lb-overlay`).
 */
export default function LeaderboardCard({
  enabled = true,
  showTitle = true,
  thankYouText = "",
}) {
  const { data: themeData } = useSelector((state) => state.theme);
  const { displayRows, loading, error, myRow } = useLeaderboardData(enabled);

  const myScore = myRow ? Number(myRow.points) || 0 : null;
  const totalPossible = (Number(themeData?.points) || 0) * (Number(themeData?.total_question) || 0);

  const accent =
    themeData?.textcolor ??
    themeData?.text_color ??
    themeData?.landing_page_title_color ??
    "#ffffff";

  const decorRaw =
    themeData?.leaderboard_decor_image ??
    themeData?.dreamcatchter_image ??
    themeData?.dreamcatcher_image ??
    "";
  const dcUrl = decorRaw ? getDvSourceImageUrl(String(decorRaw).trim()) : "";

  return (
    <div className="lb-embed" style={{ "--lb-accent": accent }}>
      {myScore != null ? (
        <div className="lb-score-summary">
          <p className="lb-score-eyebrow">Game Complete</p>
          <div
            className="lb-score-figure"
            style={themeData?.button_color ? { color: themeData.button_color } : undefined}
          >
            {myScore}
            {totalPossible > 0 ? <span className="lb-score-of"> / {totalPossible}</span> : null}
          </div>
        </div>
      ) : null}

      {thankYouText ? <p className="lb-thankyou">{thankYouText}</p> : null}

      {showTitle ? (
        <h1 className="lb-title lb-title--with-trophies">
          <span className="lb-title__trophy" aria-hidden="true">🏆</span>
          <span className="lb-title__text">Leaderboard</span>
          <span className="lb-title__trophy" aria-hidden="true">🏆</span>
        </h1>
      ) : null}

      <div className="lb-card-wrap">
        {dcUrl ? (
          <>
            <img className="lb-dc left" src={dcUrl} alt="" draggable={false} />
            <img className="lb-dc right" src={dcUrl} alt="" draggable={false} />
          </>
        ) : null}

        <div className="lb-card">
          {dcUrl ? <img className="lb-dc center" src={dcUrl} alt="" draggable={false} /> : null}

          {loading ? (
            <div className="lb-state-msg">Loading…</div>
          ) : error ? (
            <div className="lb-state-msg">{error}</div>
          ) : (
            <>
              <table className="lb-table">
                <thead>
                  <tr>
                    <th className="lb-rank">Sr</th>
                    <th className="lb-name">Name</th>
                    <th className="lb-points">Points</th>
                    <th className="lb-time">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {displayRows.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="lb-state-msg" style={{ border: "none" }}>
                        No entries yet.
                      </td>
                    </tr>
                  ) : (
                    displayRows.map((r) => (
                      <tr
                        key={`rank-${r.rank}-${r.userId ?? r.name}-${r.time}-${r.isMe ? "me" : "row"}`}
                        className={r?.isMe ? "lb-me" : ""}
                      >
                        <td className="lb-rank">
                          {r.rank != null && r.rank !== "" ? String(r.rank).padStart(2, "0") : "—"}
                        </td>
                        <td className="lb-name" title={r.name}>{r.name}</td>
                        <td className="lb-points">{r.points}</td>
                        <td className="lb-time">{r.time}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
